// "Mijn activiteiten": zoeken, toevoegen, bewerken en verwijderen.
import { memo, useMemo, useRef, useState } from 'react';
import { Clock, Info, Pencil, Plus, Search, Trash2, Zap, Sofa, TreePine, Home } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { Pill } from '../../components/ui/Pill';
import type { Activity } from '../../domain/types';
import { useActivities, useToast } from '../../state/AppProviders';
import { ConfirmDialog, type ConfirmRequest } from '../gameover/ConfirmDialog';
import { BTN_PRIMARY, BTN_TONAL, ICON_BTN, INPUT } from '../gameover/ui';
import { ActivityEditor } from './ActivityEditor';

interface RowProps {
  activity: Activity;
  onEdit: (a: Activity) => void;
  onDelete: (a: Activity) => void;
}

const Row = memo(function Row({ activity: a, onEdit, onDelete }: RowProps) {
  return (
    <li className="flex items-center gap-2 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{a.naam}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          <Pill icon={a.type === 'Buiten' ? TreePine : Home}>{a.type}</Pill>
          <Pill icon={Clock}>{a.duur} min</Pill>
          <Pill icon={a.energie === 'Veel energie' ? Zap : Sofa}>{a.energie}</Pill>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onEdit(a)}
        aria-label={`Bewerk ${a.naam}`}
        className={`${ICON_BTN} bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/15`}
      >
        <Pencil size={18} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(a)}
        aria-label={`Verwijder ${a.naam}`}
        className={`${ICON_BTN} bg-black/5 text-rose-700 hover:bg-black/10 dark:bg-white/10 dark:text-rose-300 dark:hover:bg-white/15`}
      >
        <Trash2 size={18} aria-hidden="true" />
      </button>
    </li>
  );
});

export function ActivitiesManager({ onClose }: { onClose: () => void }) {
  const { activities, customized, deleteActivity, missingDefaults, addMissingDefaults } = useActivities();
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState<{ activity?: Activity } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    const sorted = [...activities].sort((a, b) => a.naam.localeCompare(b.naam, 'nl'));
    if (!q) return sorted;
    return sorted.filter((a) => a.naam.toLowerCase().includes(q) || a.omschrijving.toLowerCase().includes(q));
  }, [activities, q]);

  const onEdit = (a: Activity) => setEditor({ activity: a });
  const onDelete = (a: Activity) =>
    setConfirm({
      title: 'Activiteit verwijderen?',
      message: '{naam} wordt uit je lijst gehaald. Dit kun je niet ongedaan maken.',
      naam: a.naam,
      confirmText: 'Verwijderen',
      onConfirm: async () => {
        try {
          await deleteActivity(a.id);
          showToast('Activiteit verwijderd');
        } catch {
          showToast('Verwijderen is niet gelukt. Probeer het opnieuw.');
        }
      },
    });

  const addMissing = async () => {
    try {
      const n = await addMissingDefaults();
      showToast(n === 1 ? '1 standaardactiviteit toegevoegd' : `${n} standaardactiviteiten toegevoegd`);
    } catch {
      showToast('Toevoegen is niet gelukt. Probeer het opnieuw.');
    }
  };

  const count = activities.length;
  const countText = q
    ? `${filtered.length} van ${count} activiteiten`
    : `${count} ${count === 1 ? 'activiteit' : 'activiteiten'}`;

  return (
    <>
      <Modal title="Mijn activiteiten" size="lg" onClose={onClose} initialFocusRef={searchRef}>
        <div className="space-y-4">
          <button type="button" onClick={() => setEditor({})} className={`${BTN_PRIMARY} w-full`}>
            <Plus size={18} aria-hidden="true" /> Nieuwe activiteit
          </button>

          {missingDefaults.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-black/5 px-4 py-3 dark:bg-white/10">
              <p className="text-sm font-semibold">
                {missingDefaults.length === 1
                  ? '1 standaardactiviteit ontbreekt'
                  : `${missingDefaults.length} standaardactiviteiten ontbreken`}
              </p>
              <button type="button" onClick={() => void addMissing()} className={BTN_TONAL}>
                Toevoegen
              </button>
            </div>
          )}

          <div className="relative">
            <Search
              size={18}
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
            />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Zoek activiteit"
              placeholder="Zoek op naam of omschrijving"
              autoComplete="off"
              className={`${INPUT} pl-11`}
            />
          </div>

          <p role="status" aria-live="polite" className="text-sm font-semibold text-slate-600 dark:text-slate-400">
            {countText}
          </p>

          {filtered.length === 0 ? (
            <p className="rounded-2xl bg-black/5 px-4 py-6 text-center text-sm text-slate-600 dark:bg-white/10 dark:text-slate-400">
              {q
                ? 'Geen activiteiten gevonden. Probeer een ander woord.'
                : 'Je hebt nog geen activiteiten. Voeg er een toe.'}
            </p>
          ) : (
            <ul
              aria-label="Activiteiten"
              className="max-h-[50vh] divide-y divide-black/5 overflow-y-auto overscroll-contain dark:divide-white/10"
            >
              {filtered.map((a) => (
                <Row key={a.id} activity={a} onEdit={onEdit} onDelete={onDelete} />
              ))}
            </ul>
          )}

          {customized && (
            <p className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
              <Info size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
              Je lijst is aangepast: updates overschrijven je activiteiten niet.
            </p>
          )}
        </div>
      </Modal>

      {editor && <ActivityEditor activity={editor.activity} onClose={() => setEditor(null)} />}
      {confirm && <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />}
    </>
  );
}

export default ActivitiesManager;
